import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";

import { exportSheet, type ExportFormat } from "../export.ts";
import {
  fetchedNote,
  kindsPresent,
  lookupPage,
  PER_FETCH,
  rowForCustomer,
  rowForTrip,
  type KindId,
  type LookupOrder,
  type LookupRow,
} from "../lookup.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { GridPager, GridToolbar, NoMatch } from "./DataGrid.tsx";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Find a customer or a trip, and open it.
 *
 * Built to `20 WP19 Support Console.dc.html`, page `lookup`, on that file's one `gridUI()`.
 *
 * **This is the one screen on this console that reads two services, and the design requires it.**
 * Document 17 WEB-0743 tolerates one listing per screen, and this screen's whole definition is a
 * search across record kinds — "Find a customer, driver, trip, order or payment by any ID or phone
 * number" — so a single listing could not be this page. Two tenant-scoped reads, each made once,
 * nothing joined: a row is a customer or a trip and the `Type` column says which, which is the
 * design's own column.
 *
 * **The search is the absence, and it is the heart of the screen.** The design's field takes "ID,
 * phone or email". **Nothing on the platform resolves a phone number or an email address to a
 * record**: CUS-01 publishes `displayName`, `locale` and `identitySubjectId` and no contact field
 * at all, and its listing filters on `status` and `identitySubjectId`. `DESIGN_QUESTIONS.md`
 * already records this against this screen. So the box filters the rows the board holds rather than
 * querying a service, and the sentence under the grid says so in as many words — because an agent
 * who types a phone number and sees nothing will otherwise conclude the customer does not exist.
 *
 * **Two of the design's five record kinds can be read and three cannot.** `customer` and `trip`
 * have a tenant-scoped collection read. `driver`, `order` and `payment` do not: DRV-01's roster
 * read belongs to the dispatch console and needs a presence join this screen has no reason to
 * make, and nothing publishes an order listing or a payment listing a support console could call.
 * The design's `Type` chips are a field of its `Search records` form, which is refused as a whole,
 * so they are not drawn on the page — the toolbar's own filter reads the kinds that are actually
 * here, which is what design rule 4 requires of every grid. `KINDS` records all five and why
 * three of them have no read, so the absence survives this file.
 *
 * **The `Last activity` column is drawn as `Last changed`.** The design's cells are `today 12:40`,
 * `in trip`, `on a job` — an instant on one row and a state on the next, which is itself the clue
 * that it is reading something this platform does not publish. `updatedAt` is when the *record*
 * changed, which for a customer is when somebody edited their profile. The column keeps the
 * design's position and takes an honest heading, because the design's heading would be false. R-35.
 *
 * **Opening a record is audited, the design says, and nothing here audits it.** The page's purpose
 * ends with that sentence. AUD-01 publishes an audit read and no audit write a console can call —
 * every ingestion route is `/internal/v1` — so a row opening on this board records nothing. Every
 * panel says so on its own footnote. This is a different kind of gap from the others on this
 * console: the rest leave a column thin, this one leaves a promise unkept, and it is the one to
 * close first.
 *
 * **No tiles, where the design draws four.** `Lookups today · 214`, `Records opened · 96`,
 * `Masked fields revealed · 7` and `Not found · 3` are all counts of *this console's own activity*,
 * which is the thing the audit service would hold and which nothing publishes a read of per day.
 * They are not counts of rows at all, so even R-04's usual reasoning does not reach them: there is
 * nothing on the platform that has ever been told a lookup happened.
 */

const customerApi = services.customer__customer_profile;
const tripApi = services.trip__trip;

export interface CustomerLookupProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** Innermost last: tenant, console, group, page. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly rows: readonly LookupRow[];
  readonly customers: number;
  readonly trips: number;
  /** True while either service answered a cursor, so Next has somewhere to go. */
  readonly more: boolean;
}

export function CustomerLookup({ transport, tenantId, crumbs }: CustomerLookupProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [kind, setKind] = useState<KindId | undefined>(undefined);
  const [order, setOrder] = useState<LookupOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);

  const load = useCallback(async () => {
    const customers = customerApi.createCustomerProfileClient(transport);
    const trips = tripApi.createTripClient(transport);
    try {
      /*
       * Both at once, and both failing together.
       *
       * `Promise.all` rather than two awaits: they are independent reads and a support agent
       * searching for somebody is waiting on the slower one either way. A partial board — customers
       * drawn, trips silently missing — would be the worse outcome, because the search would then
       * answer "not found" for a record that is there.
       */
      const [customerPage, tripPage] = await Promise.all([
        customers.listCustomerProfiles(undefined, undefined, String(PER_FETCH), undefined),
        trips.getTenantsTrips(tenantId, undefined, String(PER_FETCH), undefined),
      ]);
      setLoaded({
        rows: [
          ...customerPage.customerProfiles.map(rowForCustomer),
          ...tripPage.trips.map(rowForTrip),
        ],
        customers: customerPage.customerProfiles.length,
        trips: tripPage.trips.length,
        more: customerPage.nextAfter !== null || tripPage.nextAfter !== null,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(
        stateForError(error, ["customer-profile:read", "customer-profile:roster", "trip:read"]),
      );
      setLoaded(undefined);
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = loaded?.rows ?? [];
  /* Across everything fetched, so the panel survives a page turn or a narrowed search. */
  const selectedRow = loaded?.rows.find((row) => row.key === selectedKey);

  const view = useMemo(
    () => lookupPage(rows, { search, order, page, perPage }, kind),
    [rows, search, order, page, perPage, kind],
  );

  const viewState = failure ?? stateForList(loaded === undefined ? undefined : rows);

  const clearFilters = (): void => {
    setSearch("");
    setKind(undefined);
    setPage(1);
  };

  const note = fetchedNote(loaded?.customers, loaded?.trips, loaded?.more ?? false);

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>Record</span>
      <span>Type</span>
      <span>Last changed</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      {/*
        The page and its detail panel in one positioned container, which is what docks the panel.

        Both docking rules are written against `.board-split`: at 1240 and above
        `.board-split:has(> .detail)` makes the panel a 420px second column, and below 1240 it is
        `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s own
        `position: relative`. This board had neither — the panel was rendered inside the page, and
        on seven of these twelve boards inside the closing-note callout, where it was a flex item
        beside an `info` glyph and a sentence.

        `Settlement.tsx` and `Invoices.tsx` were already this shape, which is why they are the
        shape copied here rather than a new one. Found by the census in
        `tooling/scripts/lib/detail-panels.mjs`: this panel had never been photographed, so
        nothing compared it against the design it is built from. Which docking is right at all is
        Q-297.
      */}
      <section className="page records lookup">
        <PageHeading
          crumbs={crumbs ?? []}
          title="Customer lookup"
          source="CUS-01 · TRP-01"
          description="Find a customer, driver, trip, order or payment by any ID or phone number. Opening a record is audited."
          action={
            <button
              type="button"
              className="records__primary"
              /*
               * Drawn and refused, and this refusal is the page rather than a corner of it.
               *
               * The design's form is `Search records`: `ID, phone or email (text)` and
               * `Type (chips)`. The chips are built below and work. The field does not, because
               * **no operation on any of the 142 contracts takes a phone number or an email address
               * and answers a record.** CUS-01 has no contact field to match on; IAM-01's
               * `customer-identities` takes an email on the way *in* and publishes no read by it.
               *
               * So the search that exists is the one in the toolbar, over the rows the board already
               * holds, and this button would open a form whose one field cannot be answered. The
               * toolbar's own box is where the working search is, and the note under the grid says
               * what it does and does not cover.
               */
              disabled
              title="No operation on any contract takes a phone number or an email address and answers a record. CUS-01 publishes no contact field, and IAM-01 takes an email on registration and publishes no read by it. The working search is the box in the toolbar, over the rows this board holds. See R-35."
            >
              <span aria-hidden="true" className="records__primary-glyph">
                add
              </span>
              Search records
            </button>
          }
        />

        <div className="records__panel">
          <GridToolbar
            idPrefix="lookup"
            search={search}
            onSearch={(next) => {
              setSearch(next);
              setPage(1);
            }}
            state={kind}
            onState={(next) => {
              setKind(next as KindId | undefined);
              setPage(1);
            }}
            /*
             * The kinds present in what came back, which on this board is what the state dropdown
             * filters by.
             *
             * Every other board on this console filters by state there; this one filters by record
             * kind, because a board holding customers and trips has two state vocabularies and a
             * dropdown mixing `active` (a customer) with `in trip` (a trip) would offer a filter
             * that means two things. The kind is the one axis every row on this board shares.
             *
             * **The design's `Type (chips)` is not this control and is not drawn.** The chips are a
             * field of the `Search records` form — a modal — and that form is refused, because its
             * other field asks for a phone number or an email address that nothing resolves. Moving
             * its chips onto the page would be moving designed content, which section 1 forbids as
             * plainly as dropping it. So the filter that exists is the toolbar's own, which design
             * rule 4 requires on every grid, reading the axis this board has.
             */
            states={kindsPresent(rows)}
            order={order}
            onOrder={setOrder}
            onExport={(format: ExportFormat) => {
              exportSheet(sheetFor(view.all), format, "lookup");
            }}
          />

          <StateView
            state={viewState}
            purpose="Customers and trips appear here. Type in the box above to narrow them."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "records__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="records__rows">
              {view.rows.map((row) => (
                <div
                  key={row.key}
                  data-selected={row.key === selectedKey ? "true" : undefined}
                  role="button"
                  tabIndex={0}
                  aria-pressed={row.key === selectedKey}
                  aria-label={`Open ${row.kind} ${row.record}`}
                  onClick={() => setSelectedKey(row.key)}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    setSelectedKey(row.key);
                  }}
                  className={
                    row.state.classKey === "human"
                      ? "records__row lookup__row--human"
                      : "records__row"
                  }
                >
                  <span
                    className={
                      row.recordIsIdentifier ? "records__lead" : "records__lead records__lead--name"
                    }
                    title={row.detail.identifier}
                  >
                    {row.record}
                  </span>
                  <span className="records__cell" title={`${row.source} publishes this row.`}>
                    {row.type}
                  </span>
                  <span
                    className="records__cell"
                    title={`${row.source} publishes updatedAt, which is when this record last changed. The design's column is Last activity, which nothing publishes — see R-35.\n${row.changedAt}`}
                  >
                    {row.changedAt}
                  </span>
                  <span className="records__state">
                    <StateBadge
                      code={row.source}
                      status={row.status}
                      classKey={row.state.classKey}
                      label={row.state.label}
                      title={`${row.source} publishes ${row.status}.`}
                    />
                  </span>
                </div>
              ))}
            </div>
            {view.total === 0 ? <NoMatch onClear={clearFilters} /> : null}
            <GridPager
              idPrefix="lookup"
              page={page}
              pageCount={view.pageCount}
              perPage={perPage}
              onPerPage={(next) => {
                setPerPage(next);
                setPage(1);
              }}
              total={view.total}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => {
                if (page < view.pageCount) setPage(page + 1);
              }}
              /*
               * Never more, even where a service answered a cursor.
               *
               * Two cursors, one pager. Following either on its own would make the board hold more of
               * one kind than the other and silently reorder the merged list, so "Next" would mean
               * something different from what it means on every other board here. The note says both
               * windows and says whether there is more; widening them is a decision about the two
               * limits, not a page turn.
               */
              more={false}
              fetching={false}
            />
          </StateView>

          {note === undefined ? null : (
            <p className="records__bounded" role="status">
              {note}
            </p>
          )}
        </div>

        {/* The design's closing note, word for word, in its own `lock` kind. */}
        <div className="records__note records__note--warn">
          <span aria-hidden="true" className="records__note-glyph">
            lock
          </span>
          <span>Phone numbers stay masked. Revealing one asks for a reason and is logged.</span>
        </div>
      </section>

      {/* The selected row, in full. */}
      {selectedRow === undefined ? null : (
        <RecordDetail record={selectedRow.detail} open onClose={() => setSelectedKey(undefined)} />
      )}
    </div>
  );
}

/**
 * The rectangle an export writes: the columns on screen, in the words on screen.
 *
 * One column more than the board: `Identifier`. The `Record` cell is a name for a customer and an
 * identifier for a trip, so a file of them has no single column somebody can match a record on —
 * which is the whole purpose of exporting a lookup.
 */
function sheetFor(rows: readonly LookupRow[]): {
  header: readonly string[];
  rows: readonly (readonly string[])[];
} {
  return {
    header: ["Record", "Identifier", "Type", "Last changed", "State"],
    rows: rows.map((row) => [
      row.record,
      row.detail.identifier,
      row.type,
      row.changedAt,
      row.state.label,
    ]),
  };
}
